import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

export interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: any;
  onSave: (product: any) => Promise<void>;
}

interface RepoRow {
  name: string;
  repoUrl: string;
  branch: string;
  username: string;
  pat: string;
}

const defaultRepo = (): RepoRow => ({
  name: '',
  repoUrl: '',
  branch: 'develop',
  username: '',
  pat: '',
});

export function ProductFormDialog({
  open,
  onOpenChange,
  product,
  onSave,
}: ProductFormDialogProps) {
  const isEdit = !!product?.id;
  const [id, setId] = useState('');
  const [workspaceDir, setWorkspaceDir] = useState('');
  const [prEnvTTLMinutes, setPrEnvTTLMinutes] = useState<number>(120);
  const [repos, setRepos] = useState<RepoRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      if (product) {
        setId(product.id);
        setWorkspaceDir(product.workspaceDir || '');
        setPrEnvTTLMinutes(product.prEnvTTLMinutes ?? 120);
        setRepos(
          (product.repos || []).length > 0
            ? product.repos.map((r: any) => ({
                name: r.name || '',
                repoUrl: r.repoUrl || '',
                branch: r.branch || 'develop',
                username: r.username || '',
                pat: r.pat ? '••••••••' : '',
              }))
            : [defaultRepo()]
        );
      } else {
        setId('');
        setWorkspaceDir('');
        setPrEnvTTLMinutes(120);
        setRepos([defaultRepo()]);
      }
    }
  }, [open, product]);

  const addRepo = () => {
    setRepos((prev) => [...prev, defaultRepo()]);
  };

  const removeRepo = (index: number) => {
    setRepos((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length === 0 ? [defaultRepo()] : next;
    });
  };

  const updateRepo = (index: number, field: keyof RepoRow, value: string) => {
    setRepos((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const repoPayload = repos.map((r) => ({
        name: r.name,
        repoUrl: r.repoUrl,
        branch: r.branch || 'develop',
        ...(r.username && { username: r.username }),
        ...(r.pat && r.pat !== '••••••••' && { pat: r.pat }),
      }));

      if (isEdit) {
        await onSave({
          id,
          workspaceDir,
          prEnvTTLMinutes: prEnvTTLMinutes || undefined,
          repos: repoPayload,
        });
      } else {
        await onSave({
          id: id.trim(),
          workspaceDir: workspaceDir.trim(),
          prEnvTTLMinutes: prEnvTTLMinutes || undefined,
          repos: repoPayload,
        });
      }
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="2xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Product' : 'Create Product'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <DialogBody className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Product ID
              </label>
              <Input
                value={id}
                onChange={(e) => setId(e.target.value)}
                placeholder="e.g. my-product"
                disabled={isEdit}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Workspace Dir
              </label>
              <Input
                value={workspaceDir}
                onChange={(e) => setWorkspaceDir(e.target.value)}
                placeholder="e.g. /workspace/product"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                PR Env TTL Minutes
              </label>
              <Input
                type="number"
                min={1}
                value={prEnvTTLMinutes}
                onChange={(e) => setPrEnvTTLMinutes(Number(e.target.value) || 120)}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Repos
                </label>
                <Button type="button" variant="outline" size="sm" onClick={addRepo}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-3">
                {repos.map((repo, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 space-y-2"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Repo {i + 1}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRepo(i)}
                        className="text-red-600 hover:text-red-700 dark:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input
                        placeholder="Name"
                        value={repo.name}
                        onChange={(e) => updateRepo(i, 'name', e.target.value)}
                        required
                      />
                      <Input
                        placeholder="Repo URL"
                        value={repo.repoUrl}
                        onChange={(e) => updateRepo(i, 'repoUrl', e.target.value)}
                        required
                      />
                      <Input
                        placeholder="Branch"
                        value={repo.branch}
                        onChange={(e) => updateRepo(i, 'branch', e.target.value)}
                      />
                      <Input
                        placeholder="Username (optional)"
                        value={repo.username}
                        onChange={(e) => updateRepo(i, 'username', e.target.value)}
                      />
                      <Input
                        type="password"
                        placeholder="PAT (optional)"
                        value={repo.pat}
                        onChange={(e) => updateRepo(i, 'pat', e.target.value)}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
